import { provideRouter, Routes, withPreloading } from '@angular/router';
import { AuthGuard } from './shared/guards/auth.guard';
import { AdminGuard } from './shared/guards/admin.guard';
import { NavigationBlockManualGuard } from './shared/guards/navigation-block-manual.guard';
import { REUSE_ROUTE_DATA_KEY } from './custom-route-reuse.strategy';
import {
  PRELOAD_ROUTE_DATA_KEY,
  SelectivePreloadingStrategy,
} from './shared/routing/selective-preloading.strategy';
import { PageMetaResolver } from './seo/page-meta.resolver';
import { cityFeatureMatcher, featureOnlyMatcher } from './seo/route-matchers';
import {
  INDUSTRY_SLUG_KEY,
  SALES_FORECAST_BASE_PATH,
  SALES_FORECAST_INDEX_TITLE,
  SALES_FORECAST_ROUTES,
  SALES_FORECAST_THANK_YOU_PATH,
  salesForecastPath,
} from './sales-forecast/sales-forecast.routes-data';
import { PRICING_PAGE_TITLE } from './shared/util/consts';

export const appRoutes: Routes = [
  // SEO matching for city/feature routes
  {
    matcher: cityFeatureMatcher,
    loadComponent: () =>
      import('./map/map.component').then((c) => c.MapComponent),
    canActivate: [NavigationBlockManualGuard],
    resolve: { meta: PageMetaResolver },
  },
  {
    matcher: featureOnlyMatcher,
    loadComponent: () =>
      import('./map/map.component').then((c) => c.MapComponent),
    canActivate: [NavigationBlockManualGuard],
    resolve: { meta: PageMetaResolver },
  },

  {
    path: 'info',
    title: 'Info - Map AI',
    loadChildren: () =>
      import('./support/info.module').then((m) => m.InfoModule),
    canActivate: [NavigationBlockManualGuard],
  },
  {
    path: 'admin',
    title: 'Admin - Map AI',
    loadComponent: () =>
      import('./admin/admin.component').then((c) => c.AdminComponent),
    canMatch: [AdminGuard],
    canActivate: [AdminGuard],
  },
  {
    path: 'comparative-location-tool',
    title: 'Comparative Location Tool - Map AI',
    loadComponent: () =>
      import(
        './comparative-location-tool/comparative-location-tool.component'
      ).then((c) => c.ComparativeLocationToolComponent),
    canActivate: [NavigationBlockManualGuard, AuthGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'reports',
    title: 'Reports - Map AI',
    loadChildren: () =>
      import('./reports/reports.routes').then((r) => r.reportsRoutes),
    canActivate: [NavigationBlockManualGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true, [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'user',
    title: 'User - Map AI',
    loadChildren: () => import('./user/user.routes').then((r) => r.userRoutes),
    canActivate: [NavigationBlockManualGuard, AuthGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-in',
    loadComponent: () =>
      import('./user/sign-in/sign-in.component').then((c) => c.SignInComponent),
    canActivate: [NavigationBlockManualGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'password-recovery',
    loadComponent: () =>
      import('./user/forgot-password-page/forgot-password-page.component').then(
        (c) => c.ForgotPasswordPageComponent
      ),
  },
  {
    path: 'user/password/reset',
    loadComponent: () =>
      import(
        './user/forgot-password-page/reset-password/reset-password.component'
      ).then((c) => c.ResetPasswordComponent),
  },
  {
    path: 'sign-up',
    loadComponent: () =>
      import('./user/sign-up/sign-up.component').then((c) => c.SignUpComponent),
    canActivate: [NavigationBlockManualGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/individual',
    loadComponent: () =>
      import(
        './user/sign-up/register-individual/register-individual.component'
      ).then((c) => c.RegisterIndividualComponent),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/enterprise',
    loadComponent: () =>
      import(
        './user/sign-up/register-individual/register-individual.component'
      ).then((c) => c.RegisterIndividualComponent),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/success',
    loadComponent: () =>
      import(
        './user/sign-up/register-individual/registration-success-window/registration-success-window.component'
      ).then((c) => c.RegistrationSuccessWindowComponent),
  },
  // Sales Forecast industry hub — lists and links every industry page
  {
    path: SALES_FORECAST_BASE_PATH,
    title: SALES_FORECAST_INDEX_TITLE,
    loadComponent: () =>
      import(
        './sales-forecast/index-page/sales-forecast-index-page.component'
      ).then((c) => c.SalesForecastIndexPageComponent),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  // Sales Forecast landing pages: one static route per industry so each can
  // target its own keywords (deliberately no :industry param / dropdown).
  // Slugs/titles come from sales-forecast.routes-data.ts — the same source
  // the sitemap generator uses.
  ...SALES_FORECAST_ROUTES.map((industryRoute) => ({
    path: salesForecastPath(industryRoute.slug),
    title: industryRoute.title,
    loadComponent: () =>
      import(
        './sales-forecast/sales-forecast-page/sales-forecast-page.component'
      ).then((c) => c.SalesForecastPageComponent),
    data: { [INDUSTRY_SLUG_KEY]: industryRoute.slug },
  })),
  // Stripe success_url target for the sales-forecast checkout — noindex,
  // not linked from nav/sitemap
  {
    path: SALES_FORECAST_THANK_YOU_PATH,
    loadComponent: () =>
      import(
        './sales-forecast/thank-you-page/sales-forecast-thank-you-page.component'
      ).then((c) => c.SalesForecastThankYouPageComponent),
  },
  {
    path: 'pricing',
    // Same constant pricingPageTags uses - see PRICING_PAGE_TITLE.
    title: PRICING_PAGE_TITLE,
    loadComponent: () =>
      import('./pricing/pricing-page/pricing-page.component').then(
        (c) => c.PricingPageComponent
      ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'chat',
    loadComponent: () =>
      import('./ai-map/ai-map-page/ai-map-page.component').then(
        (c) => c.AiMapPageComponent
      ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'chat/about',
    loadComponent: () =>
      import('./ai-map/about-ai-map/about-ai-map.component').then(
        (c) => c.AboutAiMapComponent
      ),
  },
  {
    path: '',
    title: 'Map AI',
    loadComponent: () =>
      import('./map/map.component').then((c) => c.MapComponent),
    canActivate: [NavigationBlockManualGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

export const appRoutingProviders = provideRouter(
  appRoutes,
  withPreloading(SelectivePreloadingStrategy)
);
